import {
  HttpContext,
  HttpContextToken,
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, tap, throwError } from 'rxjs';

export const HTTP_ERROR_PAGE_PATH = '/page/error';

/**
 * Delay before the error page is shown, so short outages don't immediately kick the user out.
 * A successful API response within this time cancels the redirect.
 */
export const HTTP_ERROR_PAGE_DELAY_MS = 5000;

/** one pending redirect at a time: several failing requests in a row only lead to one navigation */
let pendingRedirect: ReturnType<typeof setTimeout> | null = null;

/** Navigation state passed to the error page */
export interface HttpErrorPageState {
  status: number;
  statusText: string;
  url: string | null;
  /** the router url the user was on when the error happened */
  returnUrl: string;
}

/**
 * Set this context token on a request to handle the error locally
 * instead of being redirected to the error page:
 *
 *   this.http.get(url, { context: new HttpContext().set(SKIP_HTTP_ERROR_PAGE, true) })
 */
export const SKIP_HTTP_ERROR_PAGE = new HttpContextToken<boolean>(() => false);

/** Shorthand: `this.http.get(url, { context: skipHttpErrorPage() })` */
export const skipHttpErrorPage = () => new HttpContext().set(SKIP_HTTP_ERROR_PAGE, true);

/**
 * Decides which errors lead to the error page.
 * Only network errors (status 0) and server errors (5xx).
 * 4xx errors (e.g. 401 during login, 404 on lookups) are usually handled by the caller.
 */
const shouldShowErrorPage = (error: HttpErrorResponse): boolean => error.status === 0 || error.status >= 500;

/**
 * Only API responses prove the backend is reachable again.
 * Static assets (e.g. svg icons loaded by MatIconRegistry) are served even when the API is down.
 * Matches '/api/...', 'api/...' and the v5 path ('<origin>/v5/...').
 */
const isApiRequest = (url: string): boolean => /(^|\/)(api|v5)\//.test(url);

const cancelPendingRedirect = () => {
  if (pendingRedirect !== null) {
    clearTimeout(pendingRedirect);
    pendingRedirect = null;
  }
};

export const httpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);

  return next(req).pipe(
    tap((event) => {
      if (event instanceof HttpResponse && isApiRequest(req.urlWithParams)) {
        cancelPendingRedirect();
      }
    }),
    catchError((error: unknown) => {
      if (
        error instanceof HttpErrorResponse &&
        !req.context.get(SKIP_HTTP_ERROR_PAGE) &&
        shouldShowErrorPage(error) &&
        !router.url.startsWith(HTTP_ERROR_PAGE_PATH) &&
        pendingRedirect === null
      ) {
        const state: HttpErrorPageState = {
          status: error.status,
          statusText: error.statusText,
          url: error.url,
          returnUrl: router.url,
        };
        pendingRedirect = setTimeout(() => {
          pendingRedirect = null;
          if (router.url.startsWith(HTTP_ERROR_PAGE_PATH)) {
            return;
          }
          // skipLocationChange keeps the original url in the address bar, so a reload retries the page
          router.navigate([HTTP_ERROR_PAGE_PATH], { state, skipLocationChange: true });
        }, HTTP_ERROR_PAGE_DELAY_MS);
      }
      // rethrow so callers can still clean up (spinners etc.)
      return throwError(() => error);
    }),
  );
};
